import { Column } from "primereact/column";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import localeText from "../../../../locale";
import CatalogTable from "../../components/list/CatalogTable";
import StatusBadge from "../../components/list/StatusBadge";
import useAdminList from "../../hooks/useAdminList";
import { buildSearchText } from "../../utils/adminUtils";
import { money } from "../../../Channel/channelUtils";
import { callChannel, channelService, defaultPeriod, formatDateTime } from "./adminScontiPosteUtils";
import PeriodFilter from "./PeriodFilter";

const STATUS_TONE = {
  PAID: "good",
  CREATED: "neutral",
  FAILED: "bad",
  REFUNDED: "warning",
  PARTIALLY_REFUNDED: "warning",
};

const origin = (row) => [row.utmSource, row.utmMedium, row.utmCampaign].filter(Boolean).join(" / ");

const toRow = (checkout) => ({
  ...checkout,
  dateValue: checkout.createdDate ? new Date(checkout.createdDate).getTime() : 0,
  totalValue: Number(checkout.amountTotal) || 0,
  searchText: buildSearchText([
    String(checkout.id),
    checkout.buyerName,
    checkout.buyerEmail,
    checkout.buyerUsername,
    checkout.utmSource,
    checkout.utmCampaign,
    ...(checkout.orders || []).map((o) => `${o.id} ${o.seller} ${o.sellerName}`),
  ]),
});

// Vendas do canal: um checkout (pagamento unico) com os pedidos de cada vendedor e a origem (UTMs)
const ChannelSalesSection = () => {
  const [period, setPeriod] = useState(defaultPeriod);
  const { items, loading, loadError, reload } = useAdminList(async () =>
    (await callChannel(() => channelService.adminCheckouts(period))) || [],
  );

  const rows = useMemo(() => items.map(toRow), [items]);
  const paidTotal = rows.filter((r) => r.status === "PAID").reduce((sum, r) => sum + r.totalValue, 0);

  return (
    <div className="admin-stack admin-stack--gap">
      <PeriodFilter value={period} onChange={setPeriod} onApply={reload} loading={loading} />

      {!loading && (
        <p className="admin-scp__summary">
          {localeText("adminChannelPaidTotal", { total: money(paidTotal, "EUR") })}
        </p>
      )}

      <CatalogTable
        rows={rows}
        loading={loading}
        loadError={loadError}
        onReload={reload}
        searchPlaceholder={localeText("adminChannelSearchSales")}
        sortField="dateValue"
        sortOrder={-1}
        minWidth="64rem"
      >
        <Column
          header={localeText("adminChannelCheckout")}
          field="id"
          sortable
          body={(row) => <span className="admin-num">#{row.id}</span>}
        />
        <Column
          header={localeText("adminChannelDate")}
          field="dateValue"
          sortable
          body={(row) => formatDateTime(row.paidDate || row.createdDate)}
        />
        <Column
          header={localeText("adminChannelBuyer")}
          field="buyerName"
          body={(row) =>
            row.buyerUsername || row.buyerEmail ? (
              <div className="admin-stack">
                <span className="admin-strong">{row.buyerName || row.buyerUsername}</span>
                {row.buyerEmail && <span className="admin-muted">{row.buyerEmail}</span>}
              </div>
            ) : (
              <span className="admin-muted">—</span>
            )
          }
        />
        <Column
          header={localeText("adminChannelOrders")}
          body={(row) =>
            row.orders?.length ? (
              <ul className="admin-scp__orders">
                {row.orders.map((order) => (
                  <li key={order.id}>
                    <Link to={`/dashboard-purchasesdetails?orderId=${order.id}`} className="admin-link-btn">
                      #{order.id}
                    </Link>{" "}
                    <span className="admin-muted">
                      {order.sellerName}, {money(order.amountTotal, row.currency)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="admin-muted">—</span>
            )
          }
        />
        <Column
          header={localeText("adminChannelTotal")}
          field="totalValue"
          sortable
          body={(row) => <span className="admin-num admin-strong">{money(row.totalValue, row.currency)}</span>}
        />
        <Column
          header={localeText("adminChannelStatus")}
          field="status"
          sortable
          body={(row) => (
            <StatusBadge tone={STATUS_TONE[row.status] || "neutral"}>
              {localeText(`adminChannelCheckout${row.status}`) || row.status}
            </StatusBadge>
          )}
        />
        <Column
          header={localeText("adminChannelOrigin")}
          body={(row) => (origin(row) ? origin(row) : <span className="admin-muted">{localeText("adminChannelNoOrigin")}</span>)}
        />
      </CatalogTable>
    </div>
  );
};

export default ChannelSalesSection;
